<template>
  <a-modal
    :title="title"
    width="900px"
    :open="open"
    :bodyStyle="{ height: '75vh', overflowY: 'auto', padding: '0 24px' }"
    :confirm-loading="loading"
    cancel-text="取消"
    ok-text="确定"
    centered
    destroy-on-close
    @cancel="handleCancel"
    @ok="handleOk"
  >
    <a-form
      layout="vertical"
      size="small"
    >
      <a-row :gutter="16">
        <a-col :span="12">
          <a-form-item
            label="组织"
            required
          >
            <a-select
              v-model:value="orgIds"
              mode="multiple"
              placeholder="请选择组织（可多选）"
              :options="orgOptions"
              :field-names="{ label: 'name', value: 'id' }"
              allow-clear
              show-search
              option-filter-prop="name"
            />
          </a-form-item>
        </a-col>
        <a-col :span="12">
          <a-form-item
            label="用户类型"
            required
          >
            <a-select
              v-model:value="manageType"
              placeholder="请选择用户类型"
              :options="manageTypeOptions"
            />
          </a-form-item>
        </a-col>
      </a-row>
    </a-form>

    <div class="batch-toolbar">
      <a-button
        type="primary"
        @click="handleAddRow"
      >
        <plus-outlined />
        添加
      </a-button>
      <a-upload
        :show-upload-list="false"
        :before-upload="beforeImportUpload"
        accept=".xlsx,.xls"
      >
        <a-button>
          <upload-outlined />
          导入
        </a-button>
      </a-upload>
      <a-button @click="handleDownloadTemplate">
        <download-outlined />
        下载导入模板
      </a-button>
      <a-popconfirm
        title="确认清空所有数据?"
        @confirm="handleClear()"
        v-if="dataSource.length > 0"
      >
        <a-button
          type="default"
          danger
        >
          清空
        </a-button>
      </a-popconfirm>
      <span class="batch-tip">共 {{ dataSource.length }} 条</span>
    </div>

    <s-table
      :columns="columns"
      :data-source="dataSource"
      :rowKey="rowKey"
      :bordered="true"
      size="small"
      :pagination="false"
      :scroll="{ y: 320 }"
    >
      <template #bodyCell="{ column, index, record, recordIndexs }">
        <template v-if="column.dataIndex === 'index'">
          {{ index + 1 }}
        </template>
        <template v-else-if="column.dataIndex === 'username'">
          <a-input
            v-if="record.id === currentEditId"
            v-model:value="dataSource[recordIndexs[0]].username"
            placeholder="请输入账号"
            allow-clear
          />
          <span v-else>{{ record.username }}</span>
        </template>
        <template v-else-if="column.dataIndex === 'realname'">
          <a-input
            v-if="record.id === currentEditId"
            v-model:value="dataSource[recordIndexs[0]].realname"
            placeholder="请输入姓名"
            allow-clear
          />
          <span v-else>{{ record.realname }}</span>
        </template>
        <template v-else-if="column.dataIndex === 'action'">
          <a-space>
            <a-button
              type="link"
              size="small"
              v-if="record.id !== currentEditId"
              @click="handleEdit(record)"
            >
              编辑
            </a-button>
            <a-button
              type="link"
              size="small"
              v-else
              @click="handleComplete()"
            >
              完成
            </a-button>
            <a-popconfirm
              title="确认删除该行?"
              @confirm="handleDelete(record)"
            >
              <a-button
                type="link"
                size="small"
                danger
              >
                删除
              </a-button>
            </a-popconfirm>
          </a-space>
        </template>
      </template>
    </s-table>
  </a-modal>
</template>

<script setup lang="ts">
import { DownloadOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import { read, utils } from 'xlsx';
import type { Ref } from 'vue';
import { onMounted, ref } from 'vue';
import { event_obj, useUserStore } from 'liyu-pc-base/init-plugin';
import { getApiData } from 'liyu-pc-base/api/api-model';
import { exportJsonToExcel } from 'liyu-pc-base/utils/excel';

interface Props {
  title?: string;
  record?: any;
}
const props = defineProps<Props>();
const emit = defineEmits(['cancel', 'ok']);

const open = ref(true);
const loading = ref(false);
const orgIds = ref<any[]>([]);
const manageType = ref<number>(0);
const orgOptions = ref<any[]>([]);
const dataSource = ref<any[]>([]);
const currentEditId = ref<string>('');

const userStore = useUserStore();
const current_org = userStore.currentOrg;

// 用户类型可选项：受当前账号 manage_type 约束（与 permission-user 保持一致）
const manageTypeOptions = (() => {
  const curr = userStore.person?.manage_type;
  if (curr === 2) {
    return [
      { label: '普通用户', value: 0 },
      { label: '管理员', value: 1 },
      { label: '超级管理员', value: 2 },
    ];
  } else if (curr === 1) {
    return [
      { label: '普通用户', value: 0 },
      { label: '管理员', value: 1 },
    ];
  }
  return [{ label: '普通用户', value: 0 }];
})();

const columns = [
  { title: '序号', dataIndex: 'index', width: 70, fixed: 'left' },
  { title: '账号', dataIndex: 'username', width: 220 },
  { title: '姓名', dataIndex: 'realname', width: 220 },
  { title: '操作', dataIndex: 'action', width: 140, fixed: 'right' },
];
const rowKey = 'id';

let uid = 0;
const genId = () => `row_${++uid}`;

const handleAddRow = () => {
  const id = genId();
  currentEditId.value = id;
  dataSource.value = [...dataSource.value, { id, username: '', realname: '' }];
};

const handleEdit = (record: any) => {
  currentEditId.value = record.id;
  dataSource.value = [...dataSource.value];
};
const handleComplete = () => {
  currentEditId.value = '';
  dataSource.value = [...dataSource.value];
};
const handleClear = () => {
  dataSource.value = [];
};

const handleDelete = (record: any) => {
  const idx = dataSource.value.findIndex(r => r.id === record.id);
  if (idx > -1) dataSource.value.splice(idx, 1);
  dataSource.value = [...dataSource.value];
};

// 下载导入模板（账号 / 姓名 两列）
const handleDownloadTemplate = () => {
  const percent: Ref<number> = ref(0);
  exportJsonToExcel({
    header: ['账号', '姓名'],
    data: [],
    filename: '批量创建用户导入模板',
    bookType: 'xlsx',
    autoWidth: true,
    percent,
  } as any);
};

// 导入 Excel：解析“账号 / 姓名”列，追加到表格
const beforeImportUpload = (file: any) => {
  const reader = new FileReader();
  reader.onload = (e: any) => {
    try {
      const data = new Uint8Array(e.target.result);
      const workbook = read(data, { type: 'array' });
      const firstSheet = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheet];
      const sheetData: any[][] = utils.sheet_to_json(worksheet, { header: 1 });
      const headers = (sheetData[0] || []).map((h: any) =>
        String(h ?? '')
          .trim()
          .toLowerCase(),
      );
      const userCol = headers.findIndex(h => ['账号', '用户名', 'username'].includes(h));
      const nameCol = headers.findIndex(h => ['姓名', '名字', 'realname', 'name'].includes(h));
      if (userCol === -1 && nameCol === -1) {
        message.warning('未识别到“账号/姓名”列，请使用下载的模板');
        return;
      }
      const rows = sheetData
        .slice(1)
        .filter((row: any[]) => row && row.some(c => c !== '' && c != null));
      let added = 0;
      const newRows = [];
      rows.forEach((row: any[]) => {
        const username = userCol > -1 ? String(row[userCol] ?? '').trim() : '';
        const realname = nameCol > -1 ? String(row[nameCol] ?? '').trim() : '';
        if (!username && !realname) return;
        newRows.push({ id: genId(), username, realname });
        added++;
      });
      dataSource.value = [...dataSource.value, ...newRows];
      if (added > 0) message.success(`已导入 ${added} 条`);
      else message.info('文件中没有可导入的数据');
    } catch (err) {
      console.error(err);
      message.error('解析 Excel 失败');
    }
  };
  reader.readAsArrayBuffer(file);
  return false; // 阻止自动上传
};

const handleOk = async () => {
  // 前置校验
  if (!orgIds.value || orgIds.value.length === 0) {
    message.error('请至少选择一个组织');
    return;
  }
  if (manageType.value === null || manageType.value === undefined) {
    message.error('请选择用户类型');
    return;
  }
  if (dataSource.value.length === 0) {
    message.error('请至少添加一条用户信息');
    return;
  }
  // 校验账号/姓名非空，且账号不重复
  const seen = new Set<string>();
  for (const row of dataSource.value) {
    const username = (row.username || '').trim();
    const realname = (row.realname || '').trim();
    if (!username) {
      message.error('账号不能为空，请检查表格');
      return;
    }
    if (!realname) {
      message.error(`账号 [${username}] 的姓名不能为空`);
      return;
    }
    if (seen.has(username)) {
      message.error(`账号 [${username}] 重复，请检查`);
      return;
    }
    seen.add(username);
  }

  const payload = {
    org_ids: orgIds.value,
    manage_type: manageType.value,
    user_list: dataSource.value.map(r => ({
      username: (r.username || '').trim(),
      realname: (r.realname || '').trim(),
    })),
    org_id: current_org?.id,
  };

  loading.value = true;
  try {
    await getApiData(payload, '/sys/create_batch_user');
    message.success(`批量创建完成`);
    event_obj()?.emit('refresh');
    emit('ok');
  } catch (err) {
    console.error(err);
    // 错误提示已由请求拦截器统一处理
  } finally {
    loading.value = false;
  }
};

const handleCancel = () => emit('cancel');

onMounted(async () => {
  try {
    const res: any = await getApiData({ is_pagination: false }, '/org/query_org_for_person_admin');
    const list = res?.data ?? res ?? [];
    orgOptions.value = Array.isArray(list) ? list : [];
  } catch (err) {
    console.error('加载组织列表失败', err);
  }
});
</script>

<style scoped lang="less">
.batch-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.batch-tip {
  margin-left: auto;
  color: #999;
  font-size: 12px;
}
</style>
